/**
 * TradingSetupStep Component
 *
 * Daily workflow step content for the Trading Setup section in PreTradingPhase.
 * Content-only component — parent Card provides the wrapper (CardHeader/CardBody).
 *
 * Key behaviors:
 * - Loads reference data internally (strategies, playbooks, accounts, instruments)
 * - Fires onChange on every user interaction (parent handles debounced auto-save)
 * - No Save/Discard buttons — changes are immediately lifted to parent state
 * - Risk profile selection managed here (T09): loads AccountRisk config, resolves/auto-persists
 *   default profile, emits profile changes via onProfileChange callback
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Select,
  SelectItem,
  Checkbox,
  Chip,
  Alert,
  Spinner,
  Card,
  CardHeader,
  CardBody,
} from '@enfomentis/ui';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { strategyService } from '@/services/StrategyService';
import { playbookService } from '@/services/PlaybookService';
import { accountService } from '@/services/AccountService';
import { instrumentService } from '@/services/InstrumentService';
import { InstrumentDropdown } from '@/components/shared/InstrumentExplorer';
import { isEntityActive } from '@/types/shared/EntityStatus';
import { computeStrategyRisk, computeSessionAwareRisk } from '@/utils/riskComputationUtils';
import { formatRisk } from '@/utils/riskDisplayHelpers';
import type { StrategyPlaybookPair } from '@/types/shared/TradingSetupTypes';
import type { Strategy } from '@/types/schema/Strategy';
import type { Playbook } from '@/types/schema/Playbook';
import type { Account } from '@/types/schema/Account';
import type { AccountRisk } from '@/types/schema/AccountRisk';
import type { Instrument } from '@/types/schema/Instrument';
import type {
  SessionStrategyOverride,
  SessionPlaybookOverride,
} from '@/types/schema/TradingSetupSnapshots';

// ============================================================================
// Constants
// ============================================================================

const ACCOUNT_NONE_KEY = '__none__';

// ============================================================================
// Types
// ============================================================================

export interface TradingSetupStepProps {
  activePlaybookSelections: StrategyPlaybookPair[];
  defaultAccountId: string | undefined;
  focusInstrumentIds: string[];
  selectedRiskProfileId: string | undefined;
  /** Canonical AccountRisk config for selected account (loaded by parent). */
  accountRiskConfig: AccountRisk | undefined;
  /** Whether AccountRisk config is currently loading. */
  accountRiskLoading: boolean;
  /** Whether AccountRisk config failed to load. */
  accountRiskLoadError: boolean;
  onChange: (values: {
    activePlaybookSelections: StrategyPlaybookPair[];
    defaultAccountId: string | undefined;
    focusInstrumentIds: string[];
  }) => void;
  /** Emits when risk profile selection changes (auto-resolution or user pick). */
  onProfileChange?: (profileId: string | undefined, source: 'default' | 'manual') => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onLoadErrorChange?: (hasError: boolean) => void;
  setupConfirmed: boolean;
  /** Session strategy overrides for display (session-scoped, not persisted to profile). */
  sessionStrategyOverrides?: SessionStrategyOverride[];
  /** Session playbook overrides for display (session-scoped, not persisted to profile). */
  sessionPlaybookOverrides?: SessionPlaybookOverride[];
  /** Callback when a risk value is tapped — opens risk detail modal. */
  onRiskTap?: (context: { type: 'strategy' | 'playbook'; strategyId: string; playbookId?: string }) => void;
}

/**
 * Strategy with its active playbooks, ready for rendering.
 * Strategies with zero active playbooks are filtered out.
 */
interface RenderableStrategy {
  strategy: Strategy;
  activePlaybooks: Playbook[];
}

// ============================================================================
// Component
// ============================================================================

export const TradingSetupStep: React.FC<TradingSetupStepProps> = ({
  activePlaybookSelections,
  defaultAccountId,
  focusInstrumentIds,
  selectedRiskProfileId,
  accountRiskConfig,
  accountRiskLoading,
  accountRiskLoadError,
  onChange,
  onProfileChange,
  onLoadingChange,
  onLoadErrorChange,
  setupConfirmed,
  sessionStrategyOverrides,
  sessionPlaybookOverrides,
  onRiskTap,
}) => {
  // Reference data loaded on mount
  const [renderableStrategies, setRenderableStrategies] = useState<RenderableStrategy[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Strategy card expand/collapse state
  const [expandedStrategies, setExpandedStrategies] = useState<Set<string>>(new Set());

  // Load error state for distinguishing fetch failures from empty config
  const [loadError, setLoadError] = useState(false);

  // Stable refs for callbacks to avoid stale closures
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onProfileChangeRef = useRef(onProfileChange);
  onProfileChangeRef.current = onProfileChange;

  // Stable refs for current prop values (used in callbacks to avoid stale closures)
  const pairsRef = useRef(activePlaybookSelections);
  pairsRef.current = activePlaybookSelections;

  const accountIdRef = useRef(defaultAccountId);
  accountIdRef.current = defaultAccountId;

  const focusIdsRef = useRef(focusInstrumentIds);
  focusIdsRef.current = focusInstrumentIds;

  // Per-card edit mode tracking
  const [editingCards, setEditingCards] = useState<Set<string>>(new Set());

  const handleToggleEdit = useCallback((cardKey: string) => {
    setEditingCards((prev) => {
      const next = new Set(prev);
      if (next.has(cardKey)) {
        next.delete(cardKey);
      } else {
        next.add(cardKey);
      }
      return next;
    });
  }, []);

  // Collapse all editors on false→true transition of setupConfirmed
  const prevSetupConfirmedRef = useRef(setupConfirmed);
  useEffect(() => {
    if (!prevSetupConfirmedRef.current && setupConfirmed) {
      setEditingCards(new Set());
    }
    prevSetupConfirmedRef.current = setupConfirmed;
  }, [setupConfirmed]);

  // Lift internal load state to parent for footer rendering
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    onLoadErrorChange?.(loadError);
  }, [loadError, onLoadErrorChange]);

  // ============================================================================
  // Data Loading
  // ============================================================================

  // Cancellation ref for async data loading (shared between mount and retry)
  const cancelledRef = useRef(false);

  const loadData = useCallback(async () => {
    cancelledRef.current = false;
    setIsLoading(true);
    setLoadError(false);
    try {
      const [activeStrategies, activeAccounts, allInstruments] = await Promise.all([
        strategyService.getActiveStrategies(),
        accountService.getActiveAccounts(),
        instrumentService.getAll(),
      ]);

      if (cancelledRef.current) return;

      // Load active playbooks per strategy, filter out strategies with none
      const renderable: RenderableStrategy[] = [];
      for (const strategy of activeStrategies) {
        const allPlaybooks = await playbookService.getByStrategyId(strategy.id);
        if (cancelledRef.current) return;
        const active = allPlaybooks.filter((p) => isEntityActive(p.status));
        if (active.length > 0) {
          renderable.push({ strategy, activePlaybooks: active });
        }
      }

      if (cancelledRef.current) return;

      setRenderableStrategies(renderable);
      setAccounts(activeAccounts);
      setInstruments(allInstruments.filter((i) => i.status !== 'archived'));

      // Auto-expand if single strategy
      const singleId = renderable.length === 1 ? renderable[0]?.strategy.id : undefined;
      setExpandedStrategies(singleId ? new Set([singleId]) : new Set());
    } catch (err) {
      if (!cancelledRef.current) {
        setLoadError(true);
      }
      console.error('TradingSetupStep: Failed to load reference data:', err);
    } finally {
      if (!cancelledRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadData();

    return () => {
      cancelledRef.current = true;
    };
  }, [loadData]);

  // ============================================================================
  // Single-Account Auto-Emit
  // ============================================================================

  useEffect(() => {
    if (isLoading) return;
    if (accounts.length !== 1) return;

    const currentAccountId = accountIdRef.current;
    const isValid = currentAccountId !== undefined && accounts.some((a) => a.id === currentAccountId);
    if (isValid) return;

    // Auto-emit the sole active account to sync parent state with displayed UI
    const soleAccount = accounts[0];
    if (!soleAccount) return;
    onChangeRef.current({
      activePlaybookSelections: pairsRef.current,
      defaultAccountId: soleAccount.id,
      focusInstrumentIds: focusIdsRef.current,
    });
  }, [isLoading, accounts]);

  // ============================================================================
  // Strategy-Playbook Handlers
  // ============================================================================

  const handleToggleStrategy = useCallback(
    (strategyId: string, activePlaybooks: Playbook[]) => {
      const currentPairs = pairsRef.current;
      const allSelected = activePlaybooks.every((pb) =>
        currentPairs.some((p) => p.strategyId === strategyId && p.playbookId === pb.id)
      );

      // Remove all pairs for this strategy first
      const withoutStrategy = currentPairs.filter((p) => p.strategyId !== strategyId);

      let newPairs: StrategyPlaybookPair[];
      if (allSelected) {
        // Deselect all
        newPairs = withoutStrategy;
      } else {
        // Select all active playbooks
        const addedPairs = activePlaybooks.map((pb) => ({
          strategyId,
          playbookId: pb.id,
        }));
        newPairs = [...withoutStrategy, ...addedPairs];
      }

      onChangeRef.current({
        activePlaybookSelections: newPairs,
        defaultAccountId: accountIdRef.current,
        focusInstrumentIds: focusIdsRef.current,
      });
    },
    []
  );

  const handleTogglePlaybook = useCallback((strategyId: string, playbookId: string) => {
    const currentPairs = pairsRef.current;
    const exists = currentPairs.some(
      (p) => p.strategyId === strategyId && p.playbookId === playbookId
    );

    const newPairs = exists
      ? currentPairs.filter(
          (p) => !(p.strategyId === strategyId && p.playbookId === playbookId)
        )
      : [...currentPairs, { strategyId, playbookId }];

    onChangeRef.current({
      activePlaybookSelections: newPairs,
      defaultAccountId: accountIdRef.current,
      focusInstrumentIds: focusIdsRef.current,
    });
  }, []);

  // ============================================================================
  // Account Handler
  // ============================================================================

  const handleAccountChange = useCallback((keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;
    const selected = Array.from(keys)[0] as string | undefined;

    const newAccountId =
      !selected || selected === ACCOUNT_NONE_KEY ? undefined : selected;

    onChangeRef.current({
      activePlaybookSelections: pairsRef.current,
      defaultAccountId: newAccountId,
      focusInstrumentIds: focusIdsRef.current,
    });
  }, []);

  // ============================================================================
  // Risk Profile Handler
  // ============================================================================

  const handleProfileChange = useCallback((keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;
    const selected = Array.from(keys)[0] as string | undefined;
    onProfileChangeRef.current?.(selected, 'manual');
  }, []);

  // ============================================================================
  // Instrument Handlers
  // ============================================================================

  const handleAddInstrument = useCallback((instrumentId: string) => {
    const currentIds = focusIdsRef.current;
    if (currentIds.includes(instrumentId)) return;

    onChangeRef.current({
      activePlaybookSelections: pairsRef.current,
      defaultAccountId: accountIdRef.current,
      focusInstrumentIds: [...currentIds, instrumentId],
    });
  }, []);

  const handleRemoveInstrument = useCallback((instrumentId: string) => {
    onChangeRef.current({
      activePlaybookSelections: pairsRef.current,
      defaultAccountId: accountIdRef.current,
      focusInstrumentIds: focusIdsRef.current.filter((id) => id !== instrumentId),
    });
  }, []);

  const refreshInstruments = useCallback(async () => {
    try {
      const allInstruments = await instrumentService.getAll();
      setInstruments(allInstruments.filter((i) => i.status !== 'archived'));
    } catch (err) {
      console.error('TradingSetupStep: Failed to refresh instruments:', err);
    }
  }, []);

  // ============================================================================
  // Derived State
  // ============================================================================

  const instrumentMap = useMemo(() => {
    const map = new Map<string, Instrument>();
    for (const inst of instruments) {
      map.set(inst.id, inst);
    }
    return map;
  }, [instruments]);

  // Resolved risk profile for effective risk display
  const resolvedProfile = useMemo(() => {
    if (!accountRiskConfig || !selectedRiskProfileId) return undefined;
    return accountRiskConfig.riskProfiles.find((p) => p.id === selectedRiskProfileId);
  }, [accountRiskConfig, selectedRiskProfileId]);

  // Currency for formatting risk values
  const currency = useMemo(() => {
    if (!defaultAccountId) return 'USD';
    const account = accounts.find((a) => a.id === defaultAccountId);
    return account?.baseCurrency || 'USD';
  }, [defaultAccountId, accounts]);

  // ============================================================================
  // Summary Renderers
  // ============================================================================

  const renderStrategiesSummary = () => {
    if (activePlaybookSelections.length === 0) {
      return <span className="text-sm text-default-400">No strategies selected</span>;
    }

    // Group pairs by strategyId
    const grouped = new Map<string, string[]>();
    for (const pair of activePlaybookSelections) {
      const existing = grouped.get(pair.strategyId);
      if (existing) {
        existing.push(pair.playbookId);
      } else {
        grouped.set(pair.strategyId, [pair.playbookId]);
      }
    }

    return (
      <div className="space-y-1">
        {Array.from(grouped.entries()).map(([strategyId, playbookIds]) => {
          const rs = renderableStrategies.find((r) => r.strategy.id === strategyId);
          const strategyName = rs ? rs.strategy.name : strategyId;
          // Strategy-level risk for summary header
          const sessionStratOverride = sessionStrategyOverrides?.find(
            (o) => o.strategyId === strategyId
          );
          const strategyRiskLabel = resolvedProfile
            ? formatRisk(
                sessionStratOverride
                  ? sessionStratOverride.risk
                  : computeStrategyRisk(strategyId, resolvedProfile),
                currency
              )
            : undefined;

          const playbookLabels = playbookIds.map((pbId) => {
            const pb = rs?.activePlaybooks.find((p) => p.id === pbId);
            const name = pb ? pb.name : pbId;
            if (resolvedProfile && rs) {
              const risk = computeSessionAwareRisk(strategyId, pbId, resolvedProfile, {
                ...(sessionStrategyOverrides ? { strategyOverrides: sessionStrategyOverrides } : {}),
                ...(sessionPlaybookOverrides ? { playbookOverrides: sessionPlaybookOverrides } : {}),
              });
              const hasPbOverride = sessionPlaybookOverrides?.some(
                (o) => o.strategyId === strategyId && o.playbookId === pbId
              );
              const hasStratOverride = sessionStrategyOverrides?.some(
                (o) => o.strategyId === strategyId
              );
              const isOverridden = hasPbOverride || hasStratOverride;
              if (isOverridden) {
                return { text: `${name} (${formatRisk(risk, currency)})`, overridden: true };
              }
              return { text: `${name} (${formatRisk(risk, currency)})`, overridden: false };
            }
            return { text: name, overridden: false };
          });
          return (
            <div key={strategyId} className="text-sm">
              <span className="font-medium">{strategyName}</span>
              {strategyRiskLabel && (
                <Button
                  size="sm"
                  variant="light"
                  className={`min-w-0 h-auto px-0 py-0 text-xs ml-1 ${sessionStratOverride ? 'text-warning' : 'text-default-400'}`}
                  onPress={() => onRiskTap?.({ type: 'strategy', strategyId })}
                >
                  [{strategyRiskLabel}]
                </Button>
              )}
              <span className="text-default-400"> — </span>
              {playbookLabels.map((label, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="text-default-400">, </span>}
                  <Button
                    size="sm"
                    variant="light"
                    className={`min-w-0 h-auto px-0 py-0 text-xs ${label.overridden ? 'text-warning' : 'text-default-400'}`}
                    onPress={() => {
                      const pbId = playbookIds[idx];
                      if (pbId) onRiskTap?.({ type: 'playbook', strategyId, playbookId: pbId });
                    }}
                  >
                    {label.text}
                  </Button>
                </React.Fragment>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAccountSummary = () => {
    if (!defaultAccountId) {
      return <span className="text-sm text-default-400">No account selected</span>;
    }
    const account = accounts.find((a) => a.id === defaultAccountId);
    const accountName = account ? account.name : defaultAccountId;

    // Resolve profile name for display
    let profileSuffix = '';
    if (accountRiskConfig && selectedRiskProfileId) {
      const profile = accountRiskConfig.riskProfiles.find(
        (p) => p.id === selectedRiskProfileId
      );
      if (profile) {
        const isDefault = accountRiskConfig.defaultProfileId === profile.id;
        profileSuffix = ` · ${profile.name}${isDefault ? ' (default)' : ''}`;
      }
    }

    return <span className="text-sm">{accountName}{profileSuffix}</span>;
  };

  const renderInstrumentsSummary = () => {
    if (focusInstrumentIds.length === 0) {
      return <span className="text-sm text-default-400">None</span>;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {focusInstrumentIds.map((id) => {
          const inst = instrumentMap.get(id);
          return (
            <Chip key={id} variant="flat" size="sm">
              {inst ? inst.symbol : id}
            </Chip>
          );
        })}
      </div>
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="md" />
      </div>
    );
  }

  if (loadError) {
    return (
      <Alert
        color="danger"
        title="Failed to load trading setup data"
        description="Check your connection and try again."
        endContent={
          <Button size="sm" variant="flat" color="danger" onPress={() => void loadData()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* ================================================================ */}
      {/* Section 1: Account & Risk Profile                               */}
      {/* ================================================================ */}
      <Card shadow="none" radius="sm" className="border border-default-200">
        <CardHeader className="flex items-center justify-between px-3 py-2">
          <div className="flex flex-col">
            <span className="text-sm font-medium">Account & Risk Profile</span>
            <span className="text-sm text-text-secondary">
              {editingCards.has('account')
                ? 'Select the trading account and risk profile for this session'
                : 'Trading account and risk profile for this session'}
            </span>
          </div>
          <Button
            size="sm"
            variant="light"
            onPress={() => handleToggleEdit('account')}
          >
            {editingCards.has('account') ? 'Done' : 'Edit'}
          </Button>
        </CardHeader>
        <CardBody className="border-t border-default-200 px-3 py-3">
          {editingCards.has('account') ? (
            // Editable form
            accounts.length === 0 ? (
              <Alert
                color="warning"
                title="No accounts configured"
                description="Add trading accounts in Settings first."
              />
            ) : (() => {
              const isSingleAccount = accounts.length === 1;
              const savedIsActive = defaultAccountId
                ? accounts.some((a) => a.id === defaultAccountId)
                : false;
              const displayAccountId = savedIsActive
                ? defaultAccountId
                : isSingleAccount
                  ? accounts[0]?.id
                  : undefined;

              return (
                <>
                  <Select
                    label="Account"
                    placeholder="No account selected"
                    selectedKeys={
                      new Set(displayAccountId ? [displayAccountId] : [])
                    }
                    onSelectionChange={handleAccountChange}
                    isDisabled={isSingleAccount}
                    disableAnimation
                    {...(isSingleAccount && {
                      classNames: {
                        base: '!opacity-100 !cursor-default',
                        value: '!text-text-primary',
                        selectorIcon: '!opacity-0',
                      },
                    })}
                  >
                    {[
                      ...(!isSingleAccount
                        ? [<SelectItem key={ACCOUNT_NONE_KEY}>None</SelectItem>]
                        : []),
                      ...accounts.map((account) => (
                        <SelectItem key={account.id}>{account.name}</SelectItem>
                      )),
                    ]}
                  </Select>
                  {isSingleAccount && (
                    <span className="text-xs text-text-secondary mt-1">
                      Only one active account available
                    </span>
                  )}

                  {/* Risk Profile Selector — only rendered when onProfileChange is wired (Phase 4) */}
                  {onProfileChange && displayAccountId && (() => {
                    if (accountRiskLoading) {
                      return (
                        <div className="flex items-center gap-2 mt-3">
                          <Spinner size="sm" />
                          <span className="text-xs text-text-secondary">Loading risk profiles…</span>
                        </div>
                      );
                    }

                    if (accountRiskLoadError) {
                      return (
                        <span className="text-xs text-danger mt-3 block">
                          Failed to load risk profiles
                        </span>
                      );
                    }

                    if (!accountRiskConfig) {
                      return (
                        <span className="text-xs text-default-400 mt-3 block">
                          No risk profiles configured for this account
                        </span>
                      );
                    }

                    const activeProfiles = accountRiskConfig.riskProfiles.filter(
                      (p) => isEntityActive(p.status)
                    );

                    if (activeProfiles.length === 0) {
                      return (
                        <span className="text-xs text-default-400 mt-3 block">
                          No active risk profiles for this account
                        </span>
                      );
                    }

                    const isSingleProfile = activeProfiles.length === 1;
                    const displayProfileId = selectedRiskProfileId &&
                      activeProfiles.some((p) => p.id === selectedRiskProfileId)
                        ? selectedRiskProfileId
                        : isSingleProfile
                          ? activeProfiles[0]?.id
                          : undefined;

                    return (
                      <>
                        <Select
                          label="Risk Profile"
                          placeholder="Select risk profile"
                          selectedKeys={
                            new Set(displayProfileId ? [displayProfileId] : [])
                          }
                          onSelectionChange={handleProfileChange}
                          isDisabled={isSingleProfile}
                          disableAnimation
                          className="mt-3"
                          {...(isSingleProfile && {
                            classNames: {
                              base: '!opacity-100 !cursor-default',
                              value: '!text-text-primary',
                              selectorIcon: '!opacity-0',
                            },
                          })}
                        >
                          {activeProfiles.map((profile) => (
                            <SelectItem key={profile.id}>{profile.name}</SelectItem>
                          ))}
                        </Select>
                        {isSingleProfile && (
                          <span className="text-xs text-text-secondary mt-1">
                            Only one active risk profile available
                          </span>
                        )}
                      </>
                    );
                  })()}
                </>
              );
            })()
          ) : (
            // Summary view
            renderAccountSummary()
          )}
        </CardBody>
      </Card>

      {/* ================================================================ */}
      {/* Section 2: Focus Instruments (Optional)                         */}
      {/* ================================================================ */}
      <Card shadow="none" radius="sm" className="border border-default-200">
        <CardHeader className="flex items-center justify-between px-3 py-2">
          <div className="flex flex-col">
            <span className="text-sm font-medium">Focus Instruments</span>
            <span className="text-sm text-text-secondary">
              {editingCards.has('instruments')
                ? 'Optionally declare which instruments you plan to trade today'
                : 'Instruments you plan to trade today'}
            </span>
          </div>
          <Button
            size="sm"
            variant="light"
            onPress={() => handleToggleEdit('instruments')}
          >
            {editingCards.has('instruments') ? 'Done' : 'Edit'}
          </Button>
        </CardHeader>
        <CardBody className="border-t border-default-200 px-3 py-3">
          {editingCards.has('instruments') ? (
            // Editable form
            instruments.length === 0 ? (
              <Alert
                color="warning"
                title="No instruments configured"
                description="Add instruments to your library in Settings first."
              />
            ) : (
              <>
                <InstrumentDropdown
                  value={null}
                  onChange={handleAddInstrument}
                  label="Add instrument"
                  placeholder="Select instrument to add"
                  size="sm"
                  disableAnimation
                  excludeIds={focusInstrumentIds}
                  onInstrumentsChanged={refreshInstruments}
                />

                {focusInstrumentIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {focusInstrumentIds.map((id) => {
                      const inst = instrumentMap.get(id);
                      return (
                        <Chip
                          key={id}
                          variant="flat"
                          onClose={() => handleRemoveInstrument(id)}
                        >
                          {inst ? inst.symbol : id}
                        </Chip>
                      );
                    })}
                  </div>
                )}
              </>
            )
          ) : (
            // Summary view
            renderInstrumentsSummary()
          )}
        </CardBody>
      </Card>

      {/* ================================================================ */}
      {/* Section 3: Strategy & Playbook Selection                        */}
      {/* ================================================================ */}
      <Card shadow="none" radius="sm" className="border border-default-200">
        <CardHeader className="flex items-center justify-between px-3 py-2">
          <div className="flex flex-col">
            <span className="text-sm font-medium">Strategies, Playbooks & Risk</span>
            <span className="text-sm text-text-secondary">
              {editingCards.has('strategies')
                ? 'Select strategy-playbook pairs and review effective risk for today'
                : 'Active strategy-playbook pairs and effective risk for today'}
            </span>
          </div>
          <Button
            size="sm"
            variant="light"
            onPress={() => handleToggleEdit('strategies')}
          >
            {editingCards.has('strategies') ? 'Done' : 'Edit'}
          </Button>
        </CardHeader>
        <CardBody className="border-t border-default-200 px-3 py-3">
          {editingCards.has('strategies') ? (
            // Editable form
            renderableStrategies.length === 0 ? (
              <Alert
                color="warning"
                title="No strategies with active playbooks"
                description="Configure strategies and playbooks in Settings first."
              />
            ) : (
              <div className="space-y-2">
                {renderableStrategies.map(({ strategy, activePlaybooks }) => {
                  const selectedCount = activePlaybooks.filter((pb) =>
                    activePlaybookSelections.some(
                      (p) => p.strategyId === strategy.id && p.playbookId === pb.id
                    )
                  ).length;
                  const allSelected = selectedCount === activePlaybooks.length;
                  const isIndeterminate = selectedCount > 0 && !allSelected;
                  const isExpanded = expandedStrategies.has(strategy.id);

                  return (
                    <Card
                      key={strategy.id}
                      shadow="none"
                      radius="sm"
                      className="border border-default-200"
                    >
                      <CardHeader className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            isSelected={allSelected}
                            isIndeterminate={isIndeterminate}
                            onValueChange={() =>
                              handleToggleStrategy(strategy.id, activePlaybooks)
                            }
                          >
                            <span className="text-sm font-medium">{strategy.name}</span>
                          </Checkbox>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-default-400">
                            {selectedCount}/{activePlaybooks.length}
                          </span>
                          {resolvedProfile && (() => {
                            const sessionOverride = sessionStrategyOverrides?.find(
                              (o) => o.strategyId === strategy.id
                            );
                            const strategyRisk = sessionOverride
                              ? sessionOverride.risk
                              : computeStrategyRisk(strategy.id, resolvedProfile);
                            return (
                              <Button
                                size="sm"
                                variant="light"
                                className={`min-w-0 h-auto px-1 py-0 text-xs ${sessionOverride ? 'text-warning' : 'text-default-400'}`}
                                onPress={() => onRiskTap?.({ type: 'strategy', strategyId: strategy.id })}
                              >
                                {formatRisk(strategyRisk, currency)}
                              </Button>
                            );
                          })()}
                          <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            onPress={() =>
                              setExpandedStrategies((prev) => {
                                const next = new Set(prev);
                                if (next.has(strategy.id)) {
                                  next.delete(strategy.id);
                                } else {
                                  next.add(strategy.id);
                                }
                                return next;
                              })
                            }
                            aria-label={isExpanded ? 'Collapse playbooks' : 'Expand playbooks'}
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </Button>
                        </div>
                      </CardHeader>
                      {isExpanded && (
                        <CardBody className="border-t border-default-200 p-3 space-y-1">
                          {activePlaybooks.map((pb) => {
                            const isSelected = activePlaybookSelections.some(
                              (p) =>
                                p.strategyId === strategy.id && p.playbookId === pb.id
                            );
                            return (
                              <div key={pb.id} className="flex items-center justify-between">
                                <Checkbox
                                  isSelected={isSelected}
                                  onValueChange={() =>
                                    handleTogglePlaybook(strategy.id, pb.id)
                                  }
                                >
                                  <span className="text-sm">{pb.name}</span>
                                </Checkbox>
                                {resolvedProfile && (() => {
                                  const hasPlaybookOverride = sessionPlaybookOverrides?.some(
                                    (o) => o.strategyId === strategy.id && o.playbookId === pb.id
                                  );
                                  const hasStrategyOverride = sessionStrategyOverrides?.some(
                                    (o) => o.strategyId === strategy.id
                                  );
                                  const isOverridden = hasPlaybookOverride || hasStrategyOverride;
                                  const riskColorClass = isOverridden
                                    ? 'text-warning'
                                    : isSelected ? 'text-default-600' : 'text-default-300';
                                  return (
                                    <Button
                                      size="sm"
                                      variant="light"
                                      className={`min-w-0 h-auto px-1 py-0 text-xs ${riskColorClass}`}
                                      onPress={() => onRiskTap?.({ type: 'playbook', strategyId: strategy.id, playbookId: pb.id })}
                                    >
                                      {formatRisk(
                                        computeSessionAwareRisk(strategy.id, pb.id, resolvedProfile, {
                                          ...(sessionStrategyOverrides ? { strategyOverrides: sessionStrategyOverrides } : {}),
                                          ...(sessionPlaybookOverrides ? { playbookOverrides: sessionPlaybookOverrides } : {}),
                                        }),
                                        currency
                                      )}
                                    </Button>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </CardBody>
                      )}
                    </Card>
                  );
                })}
              </div>
            )
          ) : (
            // Summary view
            renderStrategiesSummary()
          )}
        </CardBody>
      </Card>
    </div>
  );
};
